import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiLoader } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import GolfClubAutocomplete from '../GolfClubAutocomplete';
import { createGroup } from '../../services/groupService';
import { useToast } from '../../contexts/ToastContext';
import { ValidationSchemas, useValidation } from '../../utils/validation';
import posthog from 'posthog-js';
import { trackCreateGroup } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import PaywallModal from '../PaywallModal';

/**
 * Reusable form for creating a new golf group. Used by:
 *  - /create-group (full page, takes `onAddGroup` to refresh the parent groups list)
 *  - the guided onboarding wizard (renders inline, gets the created group via `onSuccess`)
 *
 * Lets the host page own surrounding chrome (page heading, success card, navigation).
 */
function CreateGroupForm({ onSuccess, onCancel, submitLabel = 'Create Group' }) {
  const toast = useToast();
  const { user } = useAuth();
  const { errors, validateField, validateForm, clearFieldError } = useValidation(ValidationSchemas.createGroup);
  const [groupName, setGroupName] = useState('');
  const [homeCourse, setHomeCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const handleGroupNameChange = (e) => {
    const value = e.target.value;
    setGroupName(value);
    if (errors.name) clearFieldError('name');
    if (value.trim()) validateField('name', value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm({ name: groupName });
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      toast.warning(firstError);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await createGroup({
        name: groupName.trim(),
        homeCourse,
      });
      if (error) {
        if (error.code === 'PLAN_CAP_GROUPS') {
          setPaywallOpen(true);
        } else {
          toast.error(error.message || 'Failed to create group');
        }
        setLoading(false);
        return;
      }
      toast.success(`Group "${data.name}" created`);
      if (user?.id && data?.id) {
        trackCreateGroup(user.id, data.id);
        posthog.group('group', data.id, {
          name: data.name,
          home_course: data.home_course_name || homeCourse?.name || null,
        });
      }
      onSuccess?.(data);
    } catch (err) {
      console.error('Error creating group:', err);
      toast.error('Failed to create group. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="groupName" className="block text-sm font-medium text-text-secondary mb-2">
          Group name
        </label>
        <input
          type="text"
          id="groupName"
          value={groupName}
          onChange={handleGroupNameChange}
          onBlur={() => validateField('name', groupName)}
          className={`w-full px-4 py-3 border rounded-md focus:ring-1 focus:border-accent bg-surface text-text-primary placeholder-text-muted ${
            errors.name ? 'border-status-error focus:ring-status-error' : 'border-border-subtle focus:ring-accent'
          }`}
          placeholder="e.g., Saturday Regulars"
          required
        />
        {errors.name && (
          <motion.p
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-status-error"
          >
            {errors.name}
          </motion.p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-text-secondary mb-2">
          Home course <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <GolfClubAutocomplete
          selectedCourse={homeCourse}
          onCourseSelect={setHomeCourse}
          placeholder="Search for a golf course…"
          className="w-full"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-app transition-colors font-medium"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="flex-1 flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? (
            <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
              {submitLabel}
            </>
          )}
        </button>
      </div>
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature="groups"
      />
    </form>
  );
}

export default CreateGroupForm;
